// src/admin/screens/collections/modals/LogContactModal.tsx
// Structured log of an agent's attempt to reach a delinquent borrower.
// Every contact lands in the `collection_contacts` table; the outcome
// + channel drive the next_promise_date / last_contact signals the
// queue view rolls up.
//
// Schema realities that differ from the plan:
//   - The contact author column is `contacted_by` (not `agent_id`).
//   - Promise amount + date columns are `ptp_amount_ngwee` + `ptp_date`
//     (not `amount_promised_ngwee` / `promise_date`).
//   - The table has no `borrower_id` column — the borrower is derived
//     from loans.user_id on the server side.
//   - `notes` is NOT NULL (empty string allowed); we default to '' so
//     zod doesn't need to make it optional.
//   - `collection_outcome` enum is the shipped set:
//     no_answer | reached_promise | reached_disputed | reached_refused
//     | wrong_number | left_message (reached_promise is used for
//     promise-to-pay instead of the draft's `promise_to_pay`).
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@admin/lib/supabase';
import { recordAudit } from '@admin/lib/audit';
import { useAuth } from '@admin/auth/useAuth';
import Modal from '@admin/components/Modal';
import Button from '@admin/components/Button';
import FormField from '@admin/components/FormField';
import { useToast } from '@admin/components/ToastProvider';
import { translateError } from '@admin/utils/translateError';
import { kwachaToNgwee } from '@/lib/types';
import { CHANNEL_LABEL, OUTCOME_LABEL } from '@admin/utils/collections';
import type { Database } from '@/lib/database.types';

type CollectionChannel = Database['public']['Enums']['collection_channel'];
type CollectionOutcome = Database['public']['Enums']['collection_outcome'];

const CHANNELS: CollectionChannel[] = ['call', 'sms', 'whatsapp', 'visit', 'email'];
const OUTCOMES: CollectionOutcome[] = [
  'no_answer',
  'reached_promise',
  'reached_disputed',
  'reached_refused',
  'wrong_number',
  'left_message',
];

const schema = z
  .object({
    channel: z.enum(['call', 'sms', 'whatsapp', 'visit', 'email']),
    outcome: z.enum([
      'no_answer',
      'reached_promise',
      'reached_disputed',
      'reached_refused',
      'wrong_number',
      'left_message',
    ]),
    amount_kwacha: z.coerce.number().min(0).optional(),
    ptp_date: z.string().optional(),
    notes: z.string().max(1000).optional(),
  })
  .refine(
    (v) => {
      // When the outcome is "reached_promise", a promise-to-pay amount
      // and date are both mandatory — a promise without specifics isn't
      // actionable by the portfolio team.
      if (v.outcome === 'reached_promise') {
        return (v.amount_kwacha ?? 0) > 0 && !!v.ptp_date;
      }
      return true;
    },
    { message: 'Promise to pay requires an amount and a promise date', path: ['amount_kwacha'] },
  );

type Values = z.infer<typeof schema>;

export interface LogContactLoan {
  id: string;
  loan_number: string;
}

interface Props {
  loan: LogContactLoan;
  onClose: (refresh: boolean) => void;
}

export default function LogContactModal({ loan, onClose }: Props) {
  const { admin } = useAuth();
  const toast = useToast();
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { channel: 'call', outcome: 'no_answer' },
  });

  const outcome = watch('outcome');
  const needsPromise = outcome === 'reached_promise';

  const submit = async (v: Values) => {
    if (!admin) return;
    setBusy(true);
    try {
      const row = {
        loan_id: loan.id,
        contacted_by: admin.id,
        contacted_at: new Date().toISOString(),
        channel: v.channel,
        outcome: v.outcome,
        ptp_amount_ngwee: needsPromise && v.amount_kwacha ? kwachaToNgwee(v.amount_kwacha) : null,
        ptp_date: needsPromise ? v.ptp_date ?? null : null,
        notes: v.notes ?? '',
      };
      const { data, error } = await supabase
        .from('collection_contacts')
        .insert(row)
        .select('id')
        .single();
      if (error) throw error;
      await recordAudit({
        action: 'collection.log_contact',
        target_type: 'loan',
        target_id: loan.id,
        payload: {
          contact_id: data.id,
          outcome: v.outcome,
          channel: v.channel,
          has_promise: needsPromise,
        },
      });
      toast({ kind: 'success', msg: 'Contact logged.' });
      await qc.invalidateQueries({ queryKey: ['collections-case', loan.id] });
      await qc.invalidateQueries({ queryKey: ['collections-queue'] });
      onClose(true);
    } catch (err) {
      toast({ kind: 'error', msg: translateError(err as Error) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open onClose={() => onClose(false)} title={`Log contact — ${loan.loan_number}`}>
      <form
        onSubmit={handleSubmit(submit)}
        style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
      >
        <FormField label="Channel" htmlFor="ch" error={errors.channel?.message} required>
          <select id="ch" {...register('channel')}>
            {CHANNELS.map((c) => (
              <option key={c} value={c}>
                {CHANNEL_LABEL[c]}
              </option>
            ))}
          </select>
        </FormField>
        <FormField label="Outcome" htmlFor="o" error={errors.outcome?.message} required>
          <select id="o" {...register('outcome')}>
            {OUTCOMES.map((o) => (
              <option key={o} value={o}>
                {OUTCOME_LABEL[o]}
              </option>
            ))}
          </select>
        </FormField>
        {needsPromise && (
          <>
            <FormField
              label="Amount promised (Kwacha)"
              htmlFor="a"
              error={errors.amount_kwacha?.message}
              required
            >
              <input id="a" type="number" min={0} step={1} {...register('amount_kwacha')} />
            </FormField>
            <FormField
              label="Promise date"
              htmlFor="d"
              error={errors.ptp_date?.message}
              required
            >
              <input id="d" type="date" {...register('ptp_date')} />
            </FormField>
          </>
        )}
        <FormField label="Notes" htmlFor="n" error={errors.notes?.message}>
          <textarea
            id="n"
            rows={3}
            {...register('notes')}
            placeholder="Context, tone, next step…"
          />
        </FormField>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button type="button" variant="secondary" onClick={() => onClose(false)} disabled={busy}>
            Cancel
          </Button>
          <Button type="submit" loading={busy}>
            Log contact
          </Button>
        </div>
      </form>
    </Modal>
  );
}
